/* Display-only currency estimates. The checkout and Paysight charge remain in USD. */
(function (root, factory) {
  'use strict';
  const currency = factory();
  if (root) root.LuvanoCurrency = currency;
  if (typeof module === 'object' && module.exports) module.exports = currency;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';

  // Shopify's selected country/currency takes priority. The browser region is a
  // fallback when the storefront defaults to USD for an international visitor.
  const countryCurrencies = Object.freeze({
    AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR', FI: 'EUR',
    FR: 'EUR', GR: 'EUR', HR: 'EUR', IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR',
    LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR', SK: 'EUR',
    GB: 'GBP', UK: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD', PL: 'PLN',
    CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', CZ: 'CZK', HU: 'HUF',
    HK: 'HKD', SG: 'SGD', JP: 'JPY', CN: 'CNY', KR: 'KRW', IN: 'INR',
    MX: 'MXN', BR: 'BRL', ZA: 'ZAR', TH: 'THB', MY: 'MYR', PH: 'PHP',
  });

  function code(value, length) {
    const normalized = String(value || '').trim().toUpperCase();
    return new RegExp('^[A-Z]{' + length + '}$').test(normalized) ? normalized : '';
  }

  function regionFromLocale(locale) {
    try { return code(new Intl.Locale(locale).region, 2); }
    catch (_error) { return ''; }
  }

  function pickCurrency(buyerCountry, buyerCurrency, locale) {
    const country = code(buyerCountry, 2);
    const selected = code(buyerCurrency, 3);
    const countryCurrency = countryCurrencies[country];
    if (selected && selected !== 'USD') return selected;
    if (country === 'US') return 'USD';
    if (country && country !== 'US' && countryCurrency) return countryCurrency;
    return countryCurrencies[regionFromLocale(locale)] || 'USD';
  }

  function parseRates(data, target, now) {
    const currency = code(target, 3);
    if (!currency || currency === 'USD') return null;
    const seriesDimensions = data && data.structure && data.structure.dimensions && data.structure.dimensions.series;
    const observationDimensions = data && data.structure && data.structure.dimensions && data.structure.dimensions.observation;
    const series = data && data.dataSets && data.dataSets[0] && data.dataSets[0].series;
    if (!Array.isArray(seriesDimensions) || !Array.isArray(observationDimensions) || !series) return null;
    const currencyIndex = seriesDimensions.findIndex(dimension => dimension.id === 'CURRENCY');
    const dateDimension = observationDimensions.find(dimension => dimension.id === 'TIME_PERIOD');
    if (currencyIndex < 0 || !dateDimension || !Array.isArray(dateDimension.values)) return null;
    const found = {};
    for (const [seriesKey, entry] of Object.entries(series)) {
      const currencyValue = seriesDimensions[currencyIndex].values[Number(seriesKey.split(':')[currencyIndex])];
      const seriesCurrency = currencyValue && currencyValue.id;
      if (seriesCurrency !== 'USD' && seriesCurrency !== currency) continue;
      const observations = entry && entry.observations;
      if (!observations) continue;
      for (const [dateIndex, values] of Object.entries(observations)) {
        const dateValue = dateDimension.values[Number(dateIndex)];
        const date = dateValue && dateValue.id;
        const rate = values && values[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(date || '') && Number.isFinite(rate) && rate > 0) {
          found[seriesCurrency] = { date, rate };
        }
      }
    }
    const usd = found.USD;
    const local = currency === 'EUR' ? { date: usd && usd.date, rate: 1 } : found[currency];
    if (!usd || !local || usd.date !== local.date) return null;
    const publishedAt = Date.parse(usd.date + 'T00:00:00Z');
    const age = (Number.isFinite(now) ? now : Date.now()) - publishedAt;
    if (!Number.isFinite(publishedAt) || age < -86400000 || age > 8 * 86400000) return null;
    return { date: usd.date, perUsd: local.rate / usd.rate };
  }

  function formatEstimate(amountCents, currency, locale, rates) {
    if (!Number.isInteger(amountCents) || amountCents <= 0 ||
        !rates || !Number.isFinite(rates.perUsd) || rates.perUsd <= 0) return '';
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency', currency, currencyDisplay: 'symbol',
      }).format(amountCents / 100 * rates.perUsd);
    } catch (_error) { return ''; }
  }

  return { pickCurrency, parseRates, formatEstimate };
});
